<template>
  <div
    class="notification"
    :class="{'notification--clickable': notification?.clickable && !hoverOnClose, 'notification--individual': this.individual}"
    ref="notification"
    @click="onClick"
    :data-notification-uuid="notification.uuid">
    <div class="notification__icon-container">
      <notification-icon :type="notification?.type" />
    </div>
    <div class="notification__message" v-html="notificationMessage" />
    <div class="notification__unread-indicator" v-if="!read" />
    <close-icon
      v-if="individual"
      @mouseenter="hoverOnClose = true"
      @mouseleave="hoverOnClose = false"
      class="notification__close" />
  </div>
</template>

<script>
import { notificationTypes } from '@/constants/notificationTypes'
import AgenciesService from '../../services/agenciesService'
import CliniciansService from '../../services/cliniciansService'
import VisitsService from '../../services/visitsService'
import { visitStatuses } from '@/constants/visitStatuses'
import CloseIcon from '../../components/icons/CloseIcon'
import { downloadFile } from '@/helpers/common'

import NotificationIcon from './NotificationIcon'

export default {
  components: {
    NotificationIcon,
    CloseIcon,
  },
  props: {
    notification: {
      default: null,
      type: Object,
    },
    router: {
      default: null,
      type: Object,
    },
    individual: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      read: this.notification.read,
      hoverOnClose: false,
    }
  },
  methods: {
    onClick(e) {
      if (
        !this.notification.clickable ||
        e.target.classList.contains('notification__close')
      ) {
        return
      }

      this.handleClick()
    },
    async handleClinician(context) {
      const { profile: uuid } = context

      try {
        const { data } = await CliniciansService.getClinicianDetails({ uuid })

        await this.routerHandler.push({
          name: 'clinicians',
          query: {
            status: data.status,
            uuid: data.uuid,
          },
        })

      } catch (e) {
        this.$notify('Something went wrong')
      }
    },
    async handleAgency(context) {
      const { agency: uuid } = context

      try {
        const { data } = await AgenciesService.getAgencyDetails({ uuid })

        await this.routerHandler.push({
          name: 'agencies',
          query: {
            status: data.status,
            uuid: data.uuid,
          },
        })
      } catch (e) {
        this.$notify('Something went wrong')
      }
    },
    async handleVisit(context, type) {
      const { visit: uuid } = context

      try {
        const { data } = await VisitsService.getVisit(uuid)

        await this.routerHandler.push({
          name: 'visits',
          query: {
            status: type === notificationTypes.VISIT_IS_COMPLETED_BY_CLINICIAN ?
              visitStatuses.COMPLETED : (type === notificationTypes.VISIT_DATE_RANGE_EXPIRED ? visitStatuses.OFFERED : visitStatuses.SCHEDULED),
            uuidVisit: data.uuid,
          },
        })
      } catch (e) {
        this.$notify('Something went wrong')
      }
    },
    async handleOffer(context) {
      const { offer: uuid } = context

      try {
        const { data } = await VisitsService.getOffer(uuid)

        await this.routerHandler.push({
          name: 'visits',
          query: {
            status: 'offers',
            uuidOffer: data.uuid,
          },
        })
      } catch (e) {
        this.$notify('Something went wrong')
      }
    },
    async handleClick() {
      if (!this.individual) {
        this.$emit('on-close')
      }

      const { type, context, ...rest } = this.notification

      if (
        type === notificationTypes.CLINICIAN_PROFILE_RECEIVED_FOR_REVIEW ||
        type === notificationTypes.CLINICIAN_PROFILE_UPDATED
      ) {
        await this.handleClinician(context)
      } else if (
        type === notificationTypes.HA_PROFILE_RECEIVED_FOR_REVIEW ||
        type === notificationTypes.HA_PROFILE_UPDATED
      ) {
        await this.handleAgency(context)
      } else if (
        type === notificationTypes.VISIT_IS_COMPLETED_BY_CLINICIAN ||
        type === notificationTypes.VISIT_SCHEDULED_DATE_EXPIRED ||
        type === notificationTypes.VISIT_DATE_RANGE_EXPIRED
      ) {
        await this.handleVisit(context, type)
      } else if (type === notificationTypes.OFFER_REJECTED_BY_CLINICIAN) {
        await this.handleOffer(context)
      } else if (
        type === notificationTypes.DICTIONARY_ATTRIBUTE_UPDATED ||
        type === notificationTypes.DICTIONARY_ATTRIBUTE_ADDED ||
        type === notificationTypes.HA_PROFILE_REJECTED
      ) {
        await this.routerHandler.push({
          name: 'profile',
        })
      } else if (type === notificationTypes.NEW_DOCUMENT_TYPE_ADDED) {
        await this.routerHandler.push({
          name: 'disciplines',
        })
      } else if (type === notificationTypes.NEW_MESSAGE_RECEIVED) {
        await this.routerHandler.push({
          name: 'chats',
          query: {
            chat: context.chat,
          },
        })
      } else if (type === notificationTypes.CLINICIAN_DOCUMENTS_ZIP_SUCCESS) {
        downloadFile({ file_url: context?.file_url || rest?.file_url })
      }
    },
  },
  computed: {
    notificationMessage() {
      if (this.notification.type === notificationTypes.CLINICIAN_DOCUMENTS_ZIP_SUCCESS) {
        return this.notification.message.replace('here', '<a class="active-link">here</a>')
      }

      return this.notification.message
    },
  },
  mounted() {
    if (!this.individual) {
      const options = {
        root: document.querySelector('.notifications-list__items'),
        rootMargin: '0px',
        threshold: 1.0,
      }
      const callback = (entries) => {
        if (entries[0].isIntersecting && !this.read) {
          this.read = true

          this.$emit('add-to-unread', this.notification.uuid)
        }
      }
      const observer = new IntersectionObserver(callback, options)
      observer.observe(this.$refs.notification)
    }
  },
  created() {
    //hack when use notification in toast
    //pass this.$router as props

    this.routerHandler = this.$router || this.router
  },
}
</script>

<style lang="scss" scoped>
.notification {
  padding: 8px;
  margin-bottom: 8px;
  border-radius: 4px;
  background-color: #fff;
  width: 100%;
  display: flex;
  align-items: center;
  cursor: default;

  &__close {
    cursor: pointer;
    width: 16px !important;
    height: 16px !important;
    margin-left: auto;
    z-index: 1;
  }

  &--clickable {
    cursor: pointer;

    &:hover {
      background-color: $gray-secondary;

      .notification__icon-container {
        background-color: #fff;
      }
    }
  }

  &--individual {
    width: 470px;
    margin-bottom: 0;
    padding: 16px 24px;
    font-size: 15px;
  }

  &__icon-container {
    width: 40px;
    height: 40px;
    border-radius: 40px;
    min-width: 40px;
    margin-right: 12px;
    background-color: $gray-secondary;
    display: flex;
    align-items: center;
    justify-content: space-around;
  }

  &__message {
    width: calc(95% - 52px);
  }

  &__unread-indicator {
    width: 8px;
    height: 8px;
    margin-left: auto;
    background-color: $green-default;
    border-radius: 8px;
  }
}
</style>
